import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./LegalPage.scss";

/** 表格数据类型：表头 + 若干行 */
interface TableData {
  head: string[];
  rows: string[][];
}

/** Section A：交易架构与各方法律地位 */
const roleTable: TableData = {
  head: ["Role & Responsibility", "Primary Responsible Party", "Legal Scope & Liability Exclusion"],
  rows: [
    [
      "Merchant of Record & Order Fulfillment",
      "Third-Party Merchant / Retailer",
      "The selling merchant retains exclusive Merchant of Record status. The merchant is solely responsible for product availability, quality, legal compliance, order fulfillment, shipping, delivery, and statutory consumer warranties. CloudStore AI (operated by BorderX Lab Inc.) is not a seller, reseller, or party to the underlying purchase contract.",
    ],
    [
      "Customer Inquiries & Dispute Management",
      "Integrating Developer & Merchant",
      "Developers integrating CloudStore AI APIs must handle end-user communications or direct order inquiries, returns, cancellations, and refunds directly to the fulfilling merchant. CloudStore AI disclaims all liability for lost shipments, merchant refusal to perform, or transaction disputes.",
    ],
    [
      "Tax Assessment, Calculation & Remittance",
      "Fulfilling Merchant & Developer",
      "Determining applicable sales tax, use tax, Value Added Tax (VAT), goods and services tax (GST), or customs duties, as well as accurate collection and remittance to taxing authorities, remains the sole duty of the Merchant and/or Developer. CloudStore AI assumes no tax withholding or reporting duties.",
    ],
  ],
};

/** Section B：数据类别 */
const dataTable: TableData = {
  head: ["Data Category", "Data Fields Included", "Processing Source & Context"],
  rows: [
    [
      "Account & Registration Details",
      "Name, business email, contact number, company name, billing details, password hashes.",
      "Direct user or developer input during account sign-up and developer portal onboarding.",
    ],
    [
      "Transactional Agent Payloads",
      "Product identifiers, cart metadata, shipping addresses, anonymized payment tokens, order numbers.",
      "Transmitted securely via API or MCP server calls during agentic checkout orchestration.",
    ],
    [
      "Technical & Telemetry Logs",
      "IP address, user-agent headers, API response times, latency metrics, session tokens, cookies.",
      "System logs automatically generated during service interactions to ensure uptime and security.",
    ],
  ],
};

/** 带加粗前缀的条款段落 */
const Clause: React.FC<{ lead: string; children: React.ReactNode }> = ({
  lead,
  children,
}) => (
  <p className="cs-legal__clause">
    <strong>{lead}</strong> {children}
  </p>
);

/** 三列表格 */
const LegalTable: React.FC<{ data: TableData }> = ({ data }) => (
  <div className="cs-legal__table-wrap">
    <table className="cs-legal__table">
      <thead>
        <tr>
          {data.head.map((h) => (
            <th key={h}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.rows.map((row, i) => (
          <tr key={i}>
            {row.map((cell, j) => (
              <td key={j} className={j === 0 ? "cs-legal__td--strong" : ""}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/**
 * Terms of Service & Privacy Policy 合并页。
 * 内容照 CloudStore AI Master Legal Framework 文档原样呈现（含标题层级与表格）。
 * 注册弹窗中的 Terms / Privacy 两个入口均跳转到本页。
 */
const LegalDocument = () => {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Terms of Service & Privacy Policy | CloudStore AI";
  }, []);

  return (
    <div className="cs-legal">
      <div className="cs-legal__inner">
        <div className="cs-legal__logo">CloudStore AI</div>
        <h1 className="cs-legal__title">
          CloudStore AI Terms of Service &amp; Privacy Policy
        </h1>

        <p>
          This Terms of Service &amp; Privacy Policy (ToS) governs the access to
          and use of <strong>CloudStore AI</strong> (operated by{" "}
          <strong>BorderX Lab Inc.</strong>, hereinafter
          referred to as &quot;CloudStore AI,&quot; &quot;Company,&quot;
          &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;). CloudStore AI
          provides an agentic commerce infrastructure, Developer Hub, API
          services, Model Context Protocol (MCP) servers, and AI-driven
          autonomous checkout protocols enabling developers, merchants, and end
          users to discover, sync, and execute seamless commerce transactions
          across global digital channels.
        </p>

        <h3 className="cs-legal__h3">Definitions</h3>
        <p>
          <strong>&quot;Developer&quot; Or &ldquo;User&rdquo;</strong> refers any
          individual or entity that integrates with, accesses, or uses the
          CloudStore AI APIs, SDKs, MCP servers, or developer tools.
        </p>
        <p>
          <strong>&ldquo;Partner&rdquo;</strong> means merchants or other
          third-party providers that integrate with CloudStore AI platform to
          provide data or other services to Developer
        </p>
        <p>
          <strong>&quot;Services&quot;</strong> means, collectively, the
          CloudStore AI APIs, SDKs, Developer Hub, MCP servers, checkout widgets,
          and demo interfaces made available by CloudStore AI.
        </p>

        {/* Section A */}
        <h2 className="cs-legal__h2">
          Section A: Developer &amp; Partner Infrastructure Agreement
        </h2>

        <h3 className="cs-legal__h3">
          1. Transaction Architecture &amp; Legal Status of Parties
        </h3>
        <p>
          CloudStore AI (operated by BorderX Lab Inc.) provides headless
          orchestration tools, proxy API routing, and AI-driven checkout
          protocols. The legal relationships, liabilities, and responsibilities
          governing all transactions executed via CloudStore AI are allocated as
          follows:
        </p>
        <LegalTable data={roleTable} />

        <h3 className="cs-legal__h3">
          Direct Merchant Checkout &amp; Agentic Authorization
        </h3>
        <p>
          Developer explicitly acknowledges and agrees that when CloudStore AI
          acts as an automated technology intermediary routing transactions or
          automated agentic checkouts to third-party merchants, CloudStore AI is
          authorized to transmit payment credentials and order payloads directly
          to target merchant payment portals. BorderX Lab Inc. and CloudStore AI
          do not control target merchant checkout environments, storefront
          security, or card processing protocols, and disclaim all liability for
          merchant-side security breaches, order holds, unauthorized secondary
          charges, or billing disputes occurring on target merchant platforms.
        </p>

        <h3 className="cs-legal__h3">
          2. Anti-Scraping Defenses, Anti-Bot Interference &amp; Order
          Cancellation Disclaimer
        </h3>
        <p>
          Developers and Partners acknowledge that CloudStore AI (operated by
          BorderX Lab Inc.) interacts dynamically with third-party merchant
          sites, marketplaces, and digital storefronts, many of which deploy
          automated rate-limiting, CAPTCHA, web scraping detection, and anti-bot
          security controls (&quot;Merchant Defenses&quot;).
        </p>
        <Clause lead="No Execution Guarantee:">
          CloudStore AI does not warrant or guarantee that any checkout intent,
          product query, or automated order request submitted through CloudStore
          AI will be accepted, processed, or fulfilled by the target merchant.
        </Clause>
        <Clause lead={'Merchant Order Cancellations ("Anti-Bot / Canceled Orders"):'}>
          Third-party merchants may unilaterally reject, freeze, flag, or cancel
          orders, proxy IP requests, or accounts executed via synthetic
          workflows, proxy nodes, or automated AI agents. CloudStore AI (operated
          by BorderX Lab Inc.) disclaims all liability for canceled orders,
          inventory holds, pricing adjustments, or account suspensions resulting
          from Merchant Defenses.
        </Clause>
        <Clause lead="Compliance with Security Laws:">
          Developers agree not to instruct, configure, or utilize CloudStore AI
          infrastructure to maliciously bypass, hack, or disable merchant
          security mechanisms, IP blocks, or access controls in violation of
          applicable computer access laws or merchant terms.
        </Clause>

        <h3 className="cs-legal__h3">
          3. Developer License, API Security &amp; Operational Standards
        </h3>
        <p>
          Subject to ongoing compliance with this Agreement, CloudStore AI
          (operated by BorderX Lab Inc.) grants Developer a limited, revocable,
          non-exclusive, non-transferable, non-sublicensable license to access
          CloudStore AI APIs, SDKs, and developer tools solely to build and
          maintain agentic commerce integrations. This license may be suspended
          immediately or terminated by CloudStore AI as set forth in the Term
          &amp; Termination section of this Terms.
        </p>
        <Clause lead="API Security:">
          Developer must safeguard API credentials and client secrets. Any
          unauthorized access or credential leak must be communicated to
          CloudStore AI within twenty-four (24) hours.
        </Clause>
        <Clause lead="Fraudulent Use Prohibition:">
          Developer shall not initiate automated test checkouts with stolen card
          tokens, fake credentials, or fraudulent user accounts.
        </Clause>
        <Clause lead="Co-Branding Requirements:">
          Unless explicitly waived under a written Enterprise Agreement with
          CloudStore AI (operated by BorderX Lab Inc.), Developer shall display
          &quot;Powered by CloudStore AI&quot; on checkout interfaces or AI agent
          decision prompts except as set forth in the applicable API
          subscription tier.
        </Clause>

        <h3 className="cs-legal__h3">
          4. Commercial Terms, Revenue Share &amp; Settlement Offset
        </h3>
        <Clause lead="Standard Payout Timeline:">
          Standard affiliate commissions or transaction fees collected by
          CloudStore AI on behalf of Developer will be remitted within thirty
          (30) days following the end of the calendar month in which clear funds
          are received by CloudStore AI (operated by BorderX Lab Inc.).
        </Clause>
        <Clause lead="Custom Revenue Share Agreements:">
          If BorderX Lab Inc. and Developer have entered into a separate written
          agreement, Enterprise SLA, or custom console split arrangement, revenue
          payouts shall be calculated and remitted strictly in accordance with
          such custom agreed terms.
        </Clause>
        <Clause lead="Adjustments & Right of Offset:">
          All payouts remain subject to deductions for cancellations, refunds,
          merchant chargebacks, payment processor fees, and fraud holds.
          CloudStore AI reserves the express right to offset any outstanding
          liabilities against pending or future Developer balances.
        </Clause>

        {/* Section B */}
        <h2 className="cs-legal__h2">
          Section B: Universal Privacy Policy &amp; AI Agent Data Protection
          Directive
        </h2>

        <h3 className="cs-legal__h3">1. Overview &amp; Data Categories</h3>
        <p>
          CloudStore AI (operated by BorderX Lab Inc.) respects individual
          privacy and handles data processed through CloudStore AI in accordance
          with global privacy principles. We collect and process the following
          data categories:
        </p>
        <LegalTable data={dataTable} />
        <p>
          CloudStore AI retains personal data only for as long as reasonably
          necessary to fulfill the purposes described in this Section B, or as
          otherwise required by applicable law, and processes such data on the
          basis of contractual necessity, compliance with legal obligations,
          and/or legitimate interests, as applicable.
        </p>

        <h3 className="cs-legal__h3">
          2. AI Agent Privacy Directives &amp; Strict Processing Limits
        </h3>
        <p>
          CloudStore AI (operated by BorderX Lab Inc.) adheres to rigorous
          privacy standards specifically designed for AI-driven agentic commerce
          environments:
        </p>
        <Clause lead="Prohibition on LLM Foundation Training:">
          CloudStore AI strictly prohibits the use of user prompt inputs,
          personal transaction data, shipping details, payment tokens, or order
          histories to train, retrain, or fine-tune public foundation AI/LLM
          models.
        </Clause>
        <Clause lead="Isolation of Post-Purchase Order Tracking Data:">
          Order status metadata, tracking links, and purchase confirmations
          retrieved on behalf of users are processed exclusively to provide order
          status updates and customer support. CloudStore AI shall not sell,
          license, or monetize order tracking data, nor utilize it for
          cross-context behavioral advertising.
        </Clause>
        <Clause lead="Ephemeral Prompt Processing:">
          AI prompts and agent intent payloads passing through CloudStore AI
          servers are processed in isolated, transient memory solely for the time
          required to execute search, price verification, and order routing
          functions.
        </Clause>
        <Clause lead="Telemetry Data Use Limitation:">
          technical and telemetry data (including IP addresses, user-agent
          headers, and cookies) is used solely for security, fraud prevention,
          service reliability, and analytics, and is not sold or used for
          cross-context behavioral advertising.
        </Clause>

        <h3 className="cs-legal__h3">
          3. Data Disclosures &amp; Third-Party Integrations
        </h3>
        <p>
          CloudStore AI (operated by BorderX Lab Inc.) does not sell personal
          data. We disclose information solely as necessary to operate the
          Services:
        </p>
        <Clause lead="Financial, Payment & Infrastructure Partners:">
          To complete authorized purchases on your behalf, payment data
          (including anonymized payment tokens or encrypted payment credentials
          provided for direct merchant checkout) and tax parameters are securely
          transmitted to PCI-DSS compliant payment processors, card issuers, or
          directly to the target merchant&rsquo;s payment checkout system.
        </Clause>
        <Clause lead="Payment Authorization & Direct Merchant Checkout Disclaimer:">
          By submitting payment credentials or authorizing automated agentic
          checkouts through CloudStore AI, User and Developer explicitly authorize
          CloudStore AI to transmit such credentials directly to target merchants,
          payment gateways, or card networks solely to execute the intended
          transaction. BorderX Lab Inc. does not control third-party merchant
          payment portals, storefront security, or merchant card processing
          systems, and disclaims all liability for unauthorized merchant-side
          transactions, merchant data breaches, card processing failures,
          unexpected holds, or billing disputes occurring on target merchant
          sites.
        </Clause>
        <Clause lead="Purpose-Limitation for User Data Storage & Disclaimer:">
          User personal data, shipping addresses, recipient contact details, and
          payment credentials submitted to or processed by CloudStore AI are
          stored and processed SOLELY AND EXCLUSIVELY for the functional purpose
          of executing user-authorized orders, fulfilling checkout intents, and
          providing post-purchase order tracking. CloudStore AI (operated by
          BorderX Lab Inc.) shall not utilize such information for secondary
          commercial profiling, unsolicited marketing, or any purpose unrelated
          to transaction fulfillment. CloudStore AI disclaims all liability for
          any secondary data processing, retention, or privacy practices
          conducted independently by third-party merchants once user data has
          been transmitted to such merchants for order completion.
        </Clause>
        <Clause lead="Legal Disclosures:">
          Information may be disclosed to comply with valid court orders,
          regulatory subpoenas, or legal enforcement requests.
        </Clause>

        <h3 className="cs-legal__h3">
          4. International Data Transfers &amp; User Rights
        </h3>
        <p>
          Data processed by CloudStore AI (operated by BorderX Lab Inc.) may be
          hosted or processed in the United States or other global jurisdictions.
          Subject to local laws (including EU GDPR, UK GDPR, and California
          CCPA/CPRA), users hold rights to access, correct, delete, or restrict
          processing of their personal data. Privacy inquiries may be submitted
          to{" "}
          <a href="mailto:cloudstoreai@borderxlab.com">
            cloudstoreai@borderxlab.com
          </a>
          .
        </p>

        {/* Section C */}
        <h2 className="cs-legal__h2">Section C: End User Platform Terms of Service</h2>

        <h3 className="cs-legal__h3">1. User Acceptance &amp; Eligibility</h3>
        <p>
          These Platform Terms of Service govern consumer and public user access
          to CloudStore AI demo interfaces, autonomous checkout widgets, and web
          applications operated by BorderX Lab Inc. Accessing or using CloudStore
          AI signifies your binding agreement to these Terms.
        </p>
        <Clause lead="Eligibility & Minimum Age:">
          You must be at least 18 years old, or the age of majority in your
          jurisdiction, to use CloudStore AI. CloudStore AI is not directed to
          children under the age of 13, and we do not knowingly collect personal
          data from children under 13. If we learn we have collected personal
          data from a child under 13, we will delete it.
        </Clause>

        <h3 className="cs-legal__h3">
          2. Prohibited Conduct &amp; Platform Integrity
        </h3>
        <p>
          When using CloudStore AI, users agree not to engage in any of the
          following restricted activities:
        </p>
        <ul className="cs-legal__list">
          <li>
            Utilizing the platform for unauthorized payment transactions,
            identity theft, or fraudulent purchases;
          </li>
          <li>
            Attempting to decompile, reverse-engineer, or extract proprietary
            algorithms or code from CloudStore AI proxy engines;
          </li>
          <li>
            Launching automated denial-of-service attacks, high-frequency stress
            tests, or disrupting platform infrastructure;
          </li>
          <li>
            Using CloudStore AI services to harvest product catalogs or build
            competing market intelligence tools without prior written
            authorization from BorderX Lab Inc.
          </li>
        </ul>

        <h3 className="cs-legal__h3">
          3. Third-Party Product Disclaimer &amp; Intellectual Property
        </h3>
        <p>
          Product information, descriptions, prices, and availability displayed
          via CloudStore AI are dynamically pulled from third-party merchant
          catalogs. CloudStore AI (operated by BorderX Lab Inc.) does not
          inspect, warrant, or endorse any third-party products. All intellectual
          property, trademarks, trade names, and software contained in CloudStore
          AI are owned exclusively by BorderX Lab Inc. or its licensors.
        </p>

        <h3 className="cs-legal__h3">
          4. Consumer Agentic Order Execution &amp; Merchant Disclaimer
        </h3>
        <p>
          By authorizing CloudStore AI or a Developer application to execute
          checkouts on your behalf, you explicitly grant CloudStore AI permission
          to submit your payment details and shipping credentials directly to the
          target merchant&apos;s checkout system. CloudStore AI is not the
          Merchant of Record, seller, or reseller of third-party goods. CloudStore
          AI stores your data solely and exclusively to execute authorized orders
          and post-purchase tracking, and disclaims all liability for payment
          failures, merchant cancellations, or secondary data processing
          conducted independently by fulfilling third-party merchants once data
          is transmitted to complete your purchase.
        </p>

        <h3 className="cs-legal__h3">
          5. Digital Millennium Copyright Act (DMCA) Take-Down Notice
        </h3>
        <p>
          CloudStore AI (operated by BorderX Lab Inc.) respects intellectual
          property rights. To submit a copyright infringement notification,
          please deliver a written DMCA notice to our designated agent at{" "}
          <a href="mailto:cloudstoreai@borderxlab.com">
            cloudstoreai@borderxlab.com
          </a>{" "}
          containing:
        </p>
        <ol className="cs-legal__list">
          <li>
            Electronic or physical signature of the copyright owner or authorized
            agent;
          </li>
          <li>
            Description of the copyrighted work claimed to have been infringed;
          </li>
          <li>
            Direct URL or description of the location of the allegedly infringing
            material;
          </li>
          <li>Your full name, physical address, telephone number, and email address;</li>
          <li>
            A statement of good faith belief that the disputed use is
            unauthorized;
          </li>
          <li>
            A statement under penalty of perjury that the information provided is
            accurate and that you are authorized to act.
          </li>
        </ol>

        {/* Section D */}
        <h2 className="cs-legal__h2">Section D: General Provisions &amp; Master Terms</h2>

        <h3 className="cs-legal__h3">1. Governing Law &amp; Dispute Resolution</h3>
        <p>
          This Terms of Service &amp; Privacy Policy and any dispute, claim, or
          controversy arising out of or relating to its subject matter,
          formation, or breach shall be governed by and construed in accordance
          with the laws of the State of California, United States, without giving
          effect to any choice or conflict of law provision or rule. Any legal
          suit, action, or proceeding arising out of or related to this Framework,
          the Platform, or the Services shall be instituted exclusively in the
          federal or state courts located in Santa Clara County, California, and
          each party irrevocably submits to the exclusive jurisdiction of such
          courts in any such suit, action, or proceeding.
        </p>

        <h3 className="cs-legal__h3">
          2. Limitation of Liability &amp; Disclaimer of Warranties
        </h3>
        <Clause lead="Disclaimer of Warranties:">
          To the maximum extent permitted by applicable law, CloudStore AI
          (operated by BorderX Lab Inc.) provides all services, APIs, SDKs,
          platform access, and demo interfaces on an &quot;as is&quot; and
          &quot;as available&quot; basis, without warranties of any kind, whether
          express, implied, statutory, or otherwise. We expressly disclaim all
          implied warranties of merchantability, fitness for a particular
          purpose, title, non-infringement, and uninterrupted or error-free
          operation.
        </Clause>
        <Clause lead="Limitation of Liability:">
          To the maximum extent permitted by applicable law, in no event shall
          BorderX Lab Inc., CloudStore AI, or its officers, directors, employees,
          affiliates, or suppliers be liable for any indirect, incidental,
          special, consequential, cover, or punitive damages (including loss of
          profits, revenue, data, goodwill, or business interruption), however
          caused, under any theory of liability. Nothing in this Section 2 limits
          either party&apos;s liability for gross negligence, willful misconduct,
          fraud, or infringement of the other party&apos;s intellectual property
          rights, to the extent such liability cannot be limited under applicable
          law.
        </Clause>
        <Clause lead="Aggregate Liability Cap:">
          In no event shall CloudStore AI&apos;s total aggregate liability arising
          out of or relating to this ToS, access to the APIs, or use of the
          services exceed the greater of: (a) the total fees paid by you to
          CloudStore AI in the twelve (12) months immediately preceding the event
          giving rise to the claim, or (b) one thousand United States dollars
          ($1000 USD).
        </Clause>

        <h3 className="cs-legal__h3">3. Amendments &amp; Modifications to Terms</h3>
        <p>
          CloudStore AI reserves the right, in its sole discretion, to modify,
          update, or revise this ToS from time to time. Any material changes will
          be communicated to registered Developers via email or through prominent
          notices posted on the Developer Hub at least fifteen (15) days prior to
          taking effect. Continued access to or use of CloudStore AI APIs, MCP
          servers, or platform services following the effective date of any
          revisions constitutes full acceptance of the updated terms.
        </p>

        <h3 className="cs-legal__h3">4. Severability &amp; Entire Agreement</h3>
        <p>
          If any provision of this Framework is found to be invalid, illegal, or
          unenforceable, that provision shall be limited or eliminated to the
          minimum extent necessary so that this Framework will otherwise remain in
          full force and effect and enforceable. This Framework, together with any
          applicable Enterprise Agreements or SLAs, constitutes the entire
          agreement between the parties regarding the subject matter hereof.
        </p>

        <h3 className="cs-legal__h3">5. Term &amp; Termination</h3>
        <p>
          This Framework commences on the date Developer or User first accesses
          the Services and continues until terminated as set forth herein. Either
          party may terminate this Framework for convenience upon thirty (30)
          days&apos; prior written notice. CloudStore AI may suspend or terminate
          Developer&apos;s access immediately upon written notice if Developer
          materially breaches this Framework and fails to cure such breach within
          fifteen (15) days of receiving notice, or immediately without notice in
          the event of fraud, illegal conduct, or a security risk to the Platform
          or its users. Upon termination, all outstanding payment obligations
          remain due, and Section 2 (Limitation of Liability &amp; Disclaimer of
          Warranties), the Indemnification section, this Governing Law &amp;
          Dispute Resolution section, and any other provision that by its nature
          should survive, will survive termination.
        </p>

        <h3 className="cs-legal__h3">6. Indemnification</h3>
        <p>
          Developer shall indemnify, defend, and hold harmless CloudStore AI,
          BorderX Lab Inc., and their officers, directors, employees, and
          affiliates from and against any third-party claims, damages,
          liabilities, and expenses (including reasonable attorneys&apos; fees)
          arising out of or relating to Developer&apos;s breach of this Framework,
          misuse of the Services, or violation of applicable law. CloudStore AI
          shall indemnify, defend, and hold harmless Developer from and against
          any third-party claims that the Services, as provided by CloudStore AI
          and used in accordance with this Framework, infringe such third
          party&apos;s United States intellectual property rights, excluding
          claims arising from Developer&apos;s modifications, third-party
          components, or merchant sites. Each party&apos;s indemnification
          obligations are subject to prompt notice, sole control of the defense by
          the indemnifying party, and reasonable cooperation by the indemnified
          party.
        </p>

        {/* Contact */}
        <h2 className="cs-legal__h2">Contact Information &amp; Legal Inquiries</h2>
        <p>
          For legal notices, contractual questions, or privacy requests regarding
          this ToS, please contact us at:
        </p>
        <ul className="cs-legal__contact">
          <li>
            <strong>Platform Name:</strong> CloudStore AI
          </li>
          <li>
            <strong>Operating Entity:</strong> BorderX Lab Inc.
          </li>
          <li>
            <strong>Legal &amp; Privacy Email:</strong>{" "}
            <a href="mailto:cloudstoreai@borderxlab.com">
              cloudstoreai@borderxlab.com
            </a>
          </li>
          <li>
            <strong>Platform URL:</strong>{" "}
            <a href="https://www.nubestore.ai/" target="_blank" rel="noreferrer">
              www.nubestore.ai
            </a>
          </li>
          <li>
            <strong>Corporate Website:</strong>{" "}
            <a
              href="https://www.borderxlab.com"
              target="_blank"
              rel="noreferrer"
            >
              https://www.borderxlab.com
            </a>
          </li>
        </ul>

        <button
          type="button"
          className="cs-legal__back"
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>
      </div>
    </div>
  );
};

export default LegalDocument;
